/*
 * HeatPumpKpiRenderer — one heat pump condensed to the KPIs an operator
 * actually asks about first: current electrical draw, COP, run state, and a
 * chip-strip of context (flow temp, outside temp, runtime, compressor starts).
 */

import type { HeatPumpKpiWidget } from '../widgets';
import type { IPointBindingSource } from '../binding';
import {
  usePoint,
  useOptionalPoint,
  numeric,
  quality,
  formatPower,
  formatRuntimeToday,
  formatRuntimeLifetime,
} from './_compound-helpers';
import { formatNumber } from '../../format/leafValue';
import './HeatPumpKpiRenderer.css';

interface Props {
  widget: HeatPumpKpiWidget;
  source: IPointBindingSource;
}

export function HeatPumpKpiRenderer({ widget, source }: Props) {
  const electrical = usePoint(source, widget.electricalPowerPoint);
  const thermal = useOptionalPoint(source, widget.thermalPowerPoint);
  const cop = useOptionalPoint(source, widget.copPoint);
  const state = usePoint(source, widget.statePoint);
  const flow = useOptionalPoint(source, widget.flowTemperaturePoint);
  const outside = useOptionalPoint(source, widget.outsideTemperaturePoint);
  const runtimeToday = useOptionalPoint(source, widget.runtimeTodayPoint);
  const runtimeLifetime = useOptionalPoint(source, widget.runtimeLifetimePoint);
  const starts = useOptionalPoint(source, widget.compressorStartsTodayPoint);

  const electricalVal = numeric(electrical.value.value?.value);
  const thermalVal = numeric(thermal?.value.value?.value);
  const flowVal = numeric(flow?.value.value?.value);
  const outsideVal = numeric(outside?.value.value?.value);
  const runtimeTodayVal = numeric(runtimeToday?.value.value?.value);
  const runtimeLifetimeVal = numeric(runtimeLifetime?.value.value?.value);
  const startsVal = numeric(starts?.value.value?.value);

  // COP: prefer bound point, else compute from thermal / electrical when both
  // are meaningful positive numbers. Avoids the noisy sub-1 numbers you get
  // when a compressor is running below its economical minimum.
  const copReported = numeric(cop?.value.value?.value);
  const copComputed =
    thermalVal != null && electricalVal != null && electricalVal > 50
      ? thermalVal / electricalVal
      : null;
  const copValue = copReported ?? copComputed;
  const copTier = copValue == null ? 'unknown' : copValue > 4 ? 'good' : copValue >= 3 ? 'warn' : 'muted';

  // Hero: electrical is always present per widget def; thermal, when set, gets
  // second billing so the operator can see both. Auto-scale W → kW.
  const heroPower = formatPower(electricalVal);
  const secondaryPower = thermalVal != null ? formatPower(thermalVal) : null;

  const electricalStale = quality(electrical.value.value) !== 'ok';
  const stateStale = quality(state.value.value) !== 'ok';
  const tileStale = electricalStale && stateStale;

  const stateRaw = state.value.value?.value;
  const stateInfo = classifyState(stateRaw);

  return (
    <div class={`hv-hp-kpi${tileStale ? ' hv-hp-kpi--stale' : ''}`}>
      <header class="hv-hp-kpi__head">
        <span class={`hv-hp-kpi__state hv-hp-kpi__state--${stateInfo.tone}`}>
          {stateInfo.label}
        </span>
        <div class="hv-hp-kpi__title">
          <div class="hv-hp-kpi__name">{widget.heatPumpName}</div>
          {widget.label && <div class="hv-hp-kpi__label">{widget.label}</div>}
        </div>
        <div class={`hv-hp-kpi__cop hv-hp-kpi__cop--${copTier}`}>
          <span class="hv-hp-kpi__cop-num">{copValue != null ? formatNumber(copValue, 2) : '—'}</span>
          <span class="hv-hp-kpi__cop-label">COP</span>
        </div>
      </header>

      <div class="hv-hp-kpi__body">
        <div class="hv-hp-kpi__hero">
          <div class="hv-hp-kpi__hero-value">
            {heroPower.text}<span>{heroPower.unit}</span>
          </div>
          <div class="hv-hp-kpi__hero-label">Elektrisch</div>
        </div>
        {secondaryPower && (
          <div class="hv-hp-kpi__hero hv-hp-kpi__hero--secondary">
            <div class="hv-hp-kpi__hero-value">
              {secondaryPower.text}<span>{secondaryPower.unit}</span>
            </div>
            <div class="hv-hp-kpi__hero-label">Thermisch</div>
          </div>
        )}
      </div>

      <footer class="hv-hp-kpi__foot">
        {flowVal != null && (
          <span class="hv-hp-kpi__stat">
            <span class="hv-hp-kpi__stat-label">Vorlauf</span>
            <span class="hv-hp-kpi__stat-value">{formatNumber(flowVal, 1)}°C</span>
          </span>
        )}
        {outsideVal != null && (
          <span class="hv-hp-kpi__stat hv-hp-kpi__stat--muted">
            <span class="hv-hp-kpi__stat-label">Außen</span>
            <span class="hv-hp-kpi__stat-value">{formatNumber(outsideVal, 1)}°C</span>
          </span>
        )}
        {runtimeTodayVal != null && (
          <span class="hv-hp-kpi__stat">
            <span class="hv-hp-kpi__stat-label">Heute</span>
            <span class="hv-hp-kpi__stat-value">{formatRuntimeToday(runtimeTodayVal)}</span>
          </span>
        )}
        {runtimeLifetimeVal != null && (
          <span class="hv-hp-kpi__stat hv-hp-kpi__stat--muted">
            <span class="hv-hp-kpi__stat-label">Gesamt</span>
            <span class="hv-hp-kpi__stat-value">{formatRuntimeLifetime(runtimeLifetimeVal)}</span>
          </span>
        )}
        {startsVal != null && (
          <span class="hv-hp-kpi__stat">
            <span class="hv-hp-kpi__stat-label">Starts</span>
            <span class="hv-hp-kpi__stat-value">{Math.round(startsVal)}×</span>
          </span>
        )}
      </footer>
    </div>
  );
}

// ---------- Helpers ------------------------------------------------------

interface StateInfo { label: string; tone: 'off' | 'muted' | 'warn' | 'ok' | 'error' | 'unknown'; }

function classifyState(v: unknown): StateInfo {
  const raw = String(v ?? '').trim();
  const s = raw.toLowerCase();
  if (s === '' || s === 'null' || s === 'undefined') return { label: '—', tone: 'unknown' };
  if (s.includes('fault') || s.includes('stoerung') || s.includes('störung') || s.includes('error'))
    return { label: raw || 'Fehler', tone: 'error' };
  if (s.includes('defrost') || s.includes('abtau'))
    return { label: raw || 'Abtauen', tone: 'warn' };
  if (s.includes('start') || s.includes('anlauf'))
    return { label: raw || 'Anlauf', tone: 'warn' };
  if (s.includes('run') || s.includes('lauf') || s.includes('heiz') || s.includes('kuehl') || s.includes('kühl'))
    return { label: raw || 'Läuft', tone: 'ok' };
  if (s.includes('standby') || s.includes('bereit'))
    return { label: raw || 'Standby', tone: 'muted' };
  if (s === 'off' || s === 'aus' || s === '0')
    return { label: raw || 'Aus', tone: 'off' };
  return { label: raw, tone: 'unknown' };
}
